import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';

import ElementHeightObserver from 'components/ElementHeightObserver';
import { MARKET_GRAPH_BASE_URL } from 'constants/locations';
import {
  MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME,
  MARKET_GRAPH_TIME_FILTER_SESSION_STORAGE_NAME
} from 'constants/marketGraph';
import { getMarketDefinitionSelectionStatuses } from 'redux/modules/marketDefinitions/selectors';
import { checkSportsMarkets, fetchGraphMarket, setIsMarketDataLoaded } from 'redux/modules/marketGraph';
import { getActiveMarkets, getGraphMarketInfo } from 'redux/modules/marketGraph/selectors';

import MarketGraphPopUp from './components/MarketGraphPopUp/MarketGraphPopUp';

export default function MarketGraphSelectionPage() {
  const dispatch = useDispatch();

  const { marketId, selectionId, handicap = 0 } = useParams();
  const activeMarkets = useSelector(getActiveMarkets);
  const market = useSelector(getGraphMarketInfo);
  const selectionStatuses = useSelector(getMarketDefinitionSelectionStatuses(marketId || ''));
  const navigate = useNavigate();

  const data =
    market?.entries?.map((entry, index) => {
      const nonRunner = selectionStatuses?.find(
        selectionStatus =>
          selectionStatus.status !== 'ACTIVE' &&
          +selectionStatus.id === +entry.selectionId &&
          +(selectionStatus.hc || 0) === +(entry.handicap || 0)
      );
      const isActive = market?.fancy ? activeMarkets[entry.marketId] || isEmpty(activeMarkets) : !nonRunner;
      const isSelected = market?.fancy
        ? marketId === entry.marketId
        : entry.selectionId.toString() === selectionId && +(entry.handicap || 0) === +handicap;

      return {
        label: entry.name,
        value: index,
        isDisabled: !isActive,
        selected: isSelected
      };
    }) || [];

  useEffect(() => {
    const marketIds = market?.entries?.map(entry => entry.marketId) || [];
    if (market?.fancy && marketIds.length) {
      dispatch(checkSportsMarkets({ marketIds: marketIds }));
    }
  }, [market?.entries]);

  const items = market?.fancy ? data.filter(item => !item.isDisabled) : data;
  const selectedItem = items.findIndex(item => item?.selected) || 0;

  const handleSave = (index: string | number) => {
    const entry = market?.entries?.[+index];
    if (entry) {
      dispatch(setIsMarketDataLoaded(false));

      const timeFilterSessionStorageValue = sessionStorage.getItem(MARKET_GRAPH_TIME_FILTER_SESSION_STORAGE_NAME);
      let timeFilterParam = '';

      if (timeFilterSessionStorageValue) {
        timeFilterParam = `&${MARKET_GRAPH_CHART_DEFAULT_FILTER_PARAM_NAME}=${timeFilterSessionStorageValue}`;
      }

      navigate(
        `${MARKET_GRAPH_BASE_URL}/${entry.marketId}/${entry.selectionId || 0}/${entry.handicap || 0}${
          location.search
        }${timeFilterParam}`,
        { replace: true }
      );
    }
  };

  useEffect(() => {
    if (marketId && market?.marketId !== marketId) {
      dispatch(fetchGraphMarket({ marketId }));
    }
  }, [marketId]);

  return (
    <ElementHeightObserver>
      <MarketGraphPopUp
        key={selectedItem}
        items={items}
        defaultValue={selectedItem}
        title={market?.orderedNameSegments.join(' - ') || ''}
        onSave={handleSave}
        isSelectionPage
        hasAutoScroll
      />
    </ElementHeightObserver>
  );
}
